import { Component, Ref, Vue } from 'vue-property-decorator'
import ModalConfirmation from '@/components/modals/ModalConfirmation.vue'
import AddCustomFilterModal from '@/components/customFilter/AddCustomFilterModal.vue'
import RenameCustomFilterModal from '@/components/customFilter/RenameCustomFilterModal.vue'
import {
    billingCompleteArray,
    BillingCustomFilter,
    BillingSidebarState,
    CustomFilterRenamed,
    initialBillingSelectedOption,
    initialBillingSidebarState
} from '@/modules/billing-module/components/sidebar/billing-sidebar.model'
import { findNodeByPropertyValue } from '@/components/sideBar/sideBarUtils'
import ExpandedSideBar from '@/components/sideBar/ExpandedSideBar.vue'
import CompactSideBar from '@/components/sideBar/CompactSideBar.vue'
import {
    UIComponentCustomFilterRequest,
    UIComponentCustomFilterResponse
} from '@/api/services/common/ui-component/custom-filter/ui-component-custom-filter.dto'
import { VtSidebarCustomConfig } from '@/api/resources/common/shared/models/common.models'
import { BillingStoreHelper } from '@/modules/billing-module/store/billing-store.helper'
import { SidebarTimeFilterType } from '@/components/sideBar/filters/time-filter/sidebar-filters-time-filter.model'
import { store } from '@/store/store'
import { BillingStoreState } from '@/modules/billing-module/store/billing-store.model'
import { StoreUtils } from '@/shared/util/store-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { HelperUtils } from '@/shared/util/helper-utils'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { BillingOperation } from '@/api/operations/models/billing-operation'

@Component({
    components: {
        ModalConfirmation,
        AddCustomFilterModal,
        RenameCustomFilterModal
    }
})
export default class BillingSidebar extends Vue {
    @Ref() readonly confirmationModal!: any
    @Ref() readonly renameCustomFilterModal!: any
    @Ref() readonly addCustomFilterModal!: any

    state: BillingSidebarState

    get disableAddFilter(): boolean {
        return this.$route.name !== 'billingGrid'
    }

    get sidebarSelectedOption(): BillingStoreState['selectedOption'] {
        return BillingStoreHelper.sidebarSelectedOption
    }

    get sidebarTimeFilter(): BillingStoreState['timeFilter'] {
        return BillingStoreHelper.sidebarTimeFilter
    }
    set sidebarTimeFilter(timeFilter: BillingStoreState['timeFilter']) {
        BillingStoreHelper.sidebarTimeFilter = timeFilter
    }

    constructor() {
        super()
        this.state = initialBillingSidebarState()
    }

    private _loadSidebar(sidebarName?: string): void {
        const sidebar = this.state.sidebars.find(s => s.name === sidebarName)
        if (sidebar) {
            this.state.sidebar = sidebar
        }
    }

    private _isOptionValid(option: any): boolean {
        return option.operations?.every((operation: BillingOperation) => HelperUtils.hasOperationInCurrentLaboratory(operation))
    }

    private async _loadSelectedOption(optionId?: string, defaultOption = false): Promise<void> {
        const options = [ ...billingCompleteArray, ...this.state.customFilterOptions ]
        const option = findNodeByPropertyValue('id', optionId, options)
        if (option) {
            if (defaultOption) {
                this.state.defaultOption = option
            }
            const validOptionToBeSelected = this._isOptionValid(option) ? option : initialBillingSelectedOption
            await this.changeSelectedOption(validOptionToBeSelected)
        }
    }

    /// custom config

    private async _loadCustomConfig(): Promise<void> {
        const customConfig =
            await UiComponentUtils.getCustomConfig<VtSidebarCustomConfig>(
                this.state.componentCode,
                store.state.labId)
        if (customConfig) {
            this._loadSidebar(customConfig.config.sidebarName)
            await this._loadSelectedOption(customConfig.config.defaultOptionId, true)

            if (customConfig.config.sidebarTimeFilter) {
                await BillingStoreHelper.setTimeFilter(customConfig.config.sidebarTimeFilter)
            }
        }
    }

    private async _saveCustomConfig(): Promise<void> {
        const config: VtSidebarCustomConfig = {
            sidebarName: this.state.sidebar.name,
            sidebarTimeFilter: this.sidebarTimeFilter,
            defaultOptionId: this.state.defaultOption?.id
        }
        await UiComponentUtils.saveCustomConfig<VtSidebarCustomConfig>(
            this.state.componentCode,
            { config },
            store.state.labId)
    }

    /// custom filter

    private async _loadCustomFilters(): Promise<void> {
        const response =
            await UiComponentUtils.getCustomFilter<BillingCustomFilter>(
                this.state.componentCode,
                store.state.labId)
        if (response) {
            this.state.customFilterOptions = response.filter(
                customFilterOption => customFilterOption.filterCriteria.operations?.every(operation => HelperUtils.hasOperationInCurrentLaboratory(operation)))
        }
    }

    private async _addCustomFilter(customFilterRequest: UIComponentCustomFilterRequest<BillingCustomFilter>): Promise<boolean> {
        if (UiComponentUtils.validateCustomFilter(customFilterRequest)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.createCustomFilter(
                        this.state.componentCode,
                        customFilterRequest,
                        store.state.labId)
                if (response) {
                    await this._loadCustomFilters()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }

    private async _removeCustomFilter(customFilterId: number): Promise<boolean> {
        try {
            StoreUtils.updateLoading(true)

            const response =
                await UiComponentUtils.removeCustomFilter(
                    this.state.componentCode,
                    customFilterId,
                    store.state.labId)
            if (response !== null) {
                await this._loadCustomFilters()

                if (customFilterId === this.sidebarSelectedOption.id) {
                    await this.changeSelectedOption(initialBillingSelectedOption)
                }
                return true
            }
        } finally {
            StoreUtils.updateLoading(false)
        }
        return false
    }

    private async _updateCustomFilter(customFilter: UIComponentCustomFilterResponse<BillingCustomFilter>): Promise<boolean> {
        if (UiComponentUtils.validateCustomFilter(customFilter)) {
            try {
                StoreUtils.updateLoading(true)

                const response =
                    await UiComponentUtils.updateCustomFilter(
                        customFilter.componentCode,
                        customFilter,
                        customFilter.id,
                        customFilter.laboratoryId)
                if (response) {
                    await this._loadCustomFilters()
                    return true
                }
            } finally {
                StoreUtils.updateLoading(false)
            }
        }
        return false
    }

    /// HOOKS ///

    async mounted(): Promise<void> {
        await this.changeSelectedOption(initialBillingSelectedOption)

        try {
            StoreUtils.updateLoading(true)
            await Promise.allSettled([
                this._loadCustomConfig(),
                this._loadCustomFilters()
            ])
        } finally {
            StoreUtils.updateLoading(false)
        }
    }

    /// EVENT HANDLERS ///

    async expand(): Promise<void> {
        this.state.sidebar = ExpandedSideBar
        await this._saveCustomConfig()

        this.$nextTick(() => this.$emit('changed-width'))
    }

    async contract(): Promise<void> {
        this.state.sidebar = CompactSideBar
        await this._saveCustomConfig()

        this.$nextTick(() => this.$emit('changed-width'))
    }

    async setDefaultOption(option: any): Promise<void> {
        this.state.defaultOption = option
        await this._saveCustomConfig()
    }

    async changeSelectedOption(selectedOption: any): Promise<void> {
        await BillingStoreHelper.setSelectedOption(selectedOption)
    }

    /// Custom filter

    onCreateCustomFilter(): void {
        this.addCustomFilterModal.show()
    }

    onRemoveCustomFilter(customFilter: UIComponentCustomFilterResponse<BillingCustomFilter>): void {
        this.confirmationModal.ask(async () => {
            if (await this._removeCustomFilter(customFilter.id)) {
                ToastUtils.showToastSuccess(this.$tc('global.filterDeleted'))
            }
        })
    }

    onRenameCustomFilter(customFilter: UIComponentCustomFilterResponse<BillingCustomFilter>): void {
        const filter: CustomFilterRenamed = {
            ...customFilter,
            label: customFilter.filterName
        }
        this.renameCustomFilterModal.show(filter)
    }

    onUpdateCustomFilter(customFilter: UIComponentCustomFilterResponse<BillingCustomFilter>): void {
        this.confirmationModal.ask(async () => {
            const filter = {
                ...customFilter,
                filterCriteria: {
                    ...customFilter.filterCriteria,
                    filterModel: BillingStoreHelper.filterModel,
                    gridType: BillingStoreHelper.gridType,
                    category: BillingStoreHelper.category,
                    timeFilter: BillingStoreHelper.sidebarTimeFilter
                }
            }
            if (await this._updateCustomFilter(filter)) {
                ToastUtils.showToastSuccess(this.$tc('global.filterUpdated'))
            }
        })
    }

    /// Time filter

    async onChangeTimeFilter(timeFilter: SidebarTimeFilterType): Promise<void> {
        await BillingStoreHelper.setTimeFilter(timeFilter)
        await this._saveCustomConfig()
    }

    /// OPERATIONS ///

    async addCustomFilter(option: { label: string }): Promise<void> {
        const filter: UIComponentCustomFilterRequest<BillingCustomFilter> = {
            filterName: option.label,
            filterCriteria: {
                filterModel: BillingStoreHelper.filterModel,
                gridType: BillingStoreHelper.gridType,
                category: BillingStoreHelper.category,
                timeFilter: BillingStoreHelper.sidebarTimeFilter,
                operations: BillingStoreHelper.sidebarSelectedOption.operations
            }
        }
        if (await this._addCustomFilter(filter)) {
            ToastUtils.showToastSuccess(this.$tc('global.filterAdded'))
        }
    }

    async renameCustomFilter(customFilter: CustomFilterRenamed): Promise<void> {
        const filter: UIComponentCustomFilterResponse<BillingCustomFilter> = {
            ...customFilter,
            filterName: customFilter.label
        }
        if (await this._updateCustomFilter(filter)) {
            ToastUtils.showToastSuccess(this.$tc('global.renamedFilter'))
        }
    }
}
